<script lang="ts">
import { h, resolveComponent, defineComponent, type PropType, type VNode } from 'vue';

import CriticalDateListResponse from '@/bundles/CriticalDates/models/CriticalDateListResponse';
import { mapCriticalDateTypeToTitle } from '@/bundles/CriticalDates/helpers';
import { formatDate } from '@/utils/filters';
import { getAccountRouteHandler } from '@/bundles/App/helpers';

import Label from '@/bundles/Common/components/Label.vue';

export default defineComponent({
  name: 'CriticalDateMobileCard',

  props: {
    item: {
      type: Object as PropType<CriticalDateListResponse>,
      required: true,
    },
    activeAccountId: {
      type: Number,
      required: true
    }
  },

  render (): VNode | VNode[] {
    const { item, activeAccountId } = this.$props;
    const VCard = resolveComponent('v-card');
    const VSpacer = resolveComponent('v-spacer');
    const RouterLink = resolveComponent('router-link');

    return h(VCard, {
      variant: 'outlined',
      ...this.$attrs,
      class: ['critical-date-mobile-card', this.$attrs.class],
    }, { default: () => [
      h('div', { class: 'd-flex align-center' }, [
        h('span', { class: 'critical-date-mobile-card__title' }, mapCriticalDateTypeToTitle(item.type)),
        h(VSpacer),
        h('div', { class: 'critical-date-mobile-card__date-chip' }, [
          h('div', 'Date'),
          h('b', { class: 'critical-date-mobile-card__date-chip-date' }, formatDate(item.date))
        ])
      ]),
      h('div', { class: 'd-flex flex-column gap-1 pa-3' }, [
        h('div', { class: 'critical-date-mobile-card__info critical-date-mobile-card__type' }, [
          h('span', { class: 'critical-date-mobile-card__info-label' }, 'Type: '),
          h('span', item.lease_date.type_name)
        ]),
        h('div', { class: 'critical-date-mobile-card__info critical-date-mobile-card__occupier' }, [
          h('span', { class: 'critical-date-mobile-card__info-label' }, 'Occupier: '),
          item.lease_date.occupier ? h(RouterLink, {
            class: 'app-link critical-date-mobile-card__occupier-name',
            to: getAccountRouteHandler({
              name: 'occupier',
              params: { id: item.lease_date.occupier._key },
              activeAccountId,
            }),
            target: '_blank',
          }, { default: () => item.lease_date.occupier.name }) : h(Label)
        ]),
        h('div', { class: 'critical-date-mobile-card__info critical-date-mobile-card__description' }, [
          h('span', { class: 'critical-date-mobile-card__info-label mr-1' }, 'Description: '),
          item.lease_date.description ? h('span', { innerHTML: item.lease_date.description }) : h(Label)
        ])
      ])
    ] })
  }
})
</script>

<style lang="scss" scoped>
// The card is this component's root, so the rule sits on it directly (Vue 2's
// `::v-deep.x` compiled onto the root; `:deep()` only reaches descendants).
.critical-date-mobile-card {
  position: relative;

  :deep(.critical-date-mobile-card__title) {
    padding-left: 12px;
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  }

  :deep(.critical-date-mobile-card__info) {
    font-size: 13px;
  }

  :deep(.critical-date-mobile-card__info-label) {
    font-weight: 600;
  }

  :deep(.critical-date-mobile-card__description) {
    display: flex;
    flex-wrap: wrap;

    p {
      margin-bottom: 0;
    }
  }

  :deep(.critical-date-mobile-card__date-chip) {
    background-color: rgb(var(--v-theme-sc-chip)); // card border color
    padding: 8px 12px;
    line-height: 16px;
    border-bottom-left-radius: 4px;

    .critical-date-mobile-card__date-chip-date {
      font-weight: 600;
    }
  }
}
</style>
